import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ArrowLeft, Users, Briefcase, DollarSign, Plus, Phone, Mail, MapPin, FileText, Edit, User, Calendar, TrendingUp, Search, X, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { useData } from '../contexts/DataContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function EmpresaDetail() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, dispatch } = useData();
  const [empresa, setEmpresa] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Get active tab from URL or default to 'overview'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl && ['overview', 'contacts', 'deals', 'comercial'].includes(tabFromUrl) 
      ? tabFromUrl 
      : 'overview';
  });
  
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [showEditEmpresaModal, setShowEditEmpresaModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [services, setServices] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  
  // Sales Rep state - now derived from leads
  const [leadCommercials, setLeadCommercials] = useState([]); // Commercials from leads
  const [allSalesReps, setAllSalesReps] = useState([]);
  
  // Lead search state
  const [contactMode, setContactMode] = useState('new'); // 'new' or 'existing'
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [selectedExistingLead, setSelectedExistingLead] = useState(null);
  
  // Update URL when tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    pipelineId: '',
    stageId: 'nuevo-lead',
  });

  const [newDeal, setNewDeal] = useState({
    services: [],
    totalValue: 0,
    estimatedCloseDate: '',
    notes: '',
    leadId: '',
  });

  const settings = state.settings || {};
  const currency = settings.currency || 'COP';

  const formatValue = (value) => {
    if (!value) return `$0 ${currency}`;
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M ${currency}`;
    }
    return `$${value.toLocaleString()} ${currency}`;
  };

  // Format number as currency for display in input
  const formatNumberAsCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('es-CO').format(value);
  };

  // Parse currency string back to number
  const parseCurrencyToNumber = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/[^\d]/g, '');
    return parseInt(cleaned) || 0;
  };

  // Get stage name from pipelines
  const getStageName = (pipelineId, stageId) => {
    if (!stageId) return 'Sin etapa';
    const pipeline = state.pipelines.find(p => p.id === pipelineId);
    if (pipeline) {
      const stage = pipeline.stages?.find(s => s.id === stageId);
      if (stage) return stage.name;
    }
    // Fallback: Format the stageId nicely
    return stageId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const loadEmpresa = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getEmpresa(empresaId);
      setEmpresa(data.empresa);
      setContacts(data.contacts || []);
      setDeals(data.deals || []);
      setStats(data.stats || {});
      
      // Get unique commercials from leads (via assignedTo field)
      const contacts_list = data.contacts || [];
      const assignedToIds = [...new Set(contacts_list.map(c => c.assignedTo).filter(Boolean))];
      
      // Get all users to match names
      const allUsers = await api.getUsers();
      const commercialsFromLeads = assignedToIds
        .map(id => allUsers.find(u => u.id === id))
        .filter(Boolean);
      
      setLeadCommercials(commercialsFromLeads);
    } catch (error) {
      toast.error('Error al cargar la empresa');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const loadAllSalesReps = async () => {
    try {
      const data = await api.getUsers();
      setAllSalesReps(data.filter(u => u.role === 'comercial'));
    } catch (error) {
      console.error('Error loading sales reps:', error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await api.getServices();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadPipelines = async () => {
    try {
      const data = await api.getPipelines();
      setPipelines(data);
      if (data.length > 0 && !newContact.pipelineId) {
        setNewContact(prev => ({ ...prev, pipelineId: data[0].id }));
      }
    } catch (error) {
      console.error('Error loading pipelines:', error);
    }
  };

  useEffect(() => {
    loadEmpresa();
    loadServices();
    loadPipelines();
    loadAllSalesReps();
  }, [loadEmpresa]);

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!newContact.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!newContact.pipelineId) {
      toast.error('Seleccione un embudo');
      return;
    }

    try {
      await api.addContactToEmpresa(empresaId, newContact);
      toast.success('Lead agregado exitosamente');
      setShowNewContactModal(false);
      setNewContact({
        name: '',
        email: '',
        phone: '',
        pipelineId: pipelines[0]?.id || '',
        stageId: 'nuevo-lead',
      });
      loadEmpresa();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al agregar lead');
    }
  };

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    if (newDeal.services.length === 0) {
      toast.error('Agregue al menos un servicio');
      return;
    }
    if (!newDeal.leadId) {
      toast.error('Seleccione un lead responsable');
      return;
    }

    try {
      const selectedContact = contacts.find(c => c.id === newDeal.leadId);
      const dealData = {
        empresaId: empresaId,
        empresaName: empresa.name,
        leadId: newDeal.leadId,
        leadName: selectedContact?.name || '',
        services: newDeal.services,
        totalValue: newDeal.services.reduce((acc, s) => acc + (s.value * s.quantity), 0),
        estimatedCloseDate: newDeal.estimatedCloseDate,
        notes: newDeal.notes,
        status: 'negotiation',
      };
      
      await api.createDeal(dealData);
      toast.success('Negocio creado exitosamente');
      setShowNewDealModal(false);
      setNewDeal({
        services: [],
        totalValue: 0,
        estimatedCloseDate: '',
        notes: '',
        leadId: '',
      });
      loadEmpresa();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al crear negocio');
    }
  };

  // Search leads for association
  const searchLeads = async (query) => {
    if (!query || query.length < 2) {
      setLeadSearchResults([]);
      return;
    }
    setSearchingLeads(true);
    try {
      // Correct parameter order: (page, limit, pipelineId, search)
      const data = await api.getLeads(1, 20, null, query);
      // Filter out leads already associated with this empresa or any empresa
      const filteredLeads = (data.leads || []).filter(
        lead => !contacts.find(c => c.id === lead.id) && !lead.empresaId
      );
      setLeadSearchResults(filteredLeads);
    } catch (error) {
      console.error('Error searching leads:', error);
    } finally {
      setSearchingLeads(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (leadSearchQuery) {
        searchLeads(leadSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [leadSearchQuery]);

  // Associate existing lead to empresa
  const handleAssociateExistingLead = async () => {
    if (!selectedExistingLead) {
      toast.error('Seleccione un lead');
      return;
    }
    try {
      await api.associateLeadToEmpresa(empresaId, selectedExistingLead.id);
      toast.success('Lead asociado exitosamente');
      setShowNewContactModal(false);
      setContactMode('new');
      setLeadSearchQuery('');
      setLeadSearchResults([]);
      setSelectedExistingLead(null);
      loadEmpresa();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al asociar lead');
    }
  };

  // Edit deal functions
  const openEditDealModal = (deal) => {
    setEditingDeal({
      ...deal,
      services: deal.services || [],
    });
    setShowEditDealModal(true);
  };

  const handleUpdateDeal = async (e) => {
    e.preventDefault();
    if (!editingDeal) return;

    try {
      await api.updateDeal(editingDeal.id, {
        services: editingDeal.services,
        totalValue: editingDeal.services.reduce((acc, s) => acc + ((s.value || s.minPrice || 0) * (s.quantity || 1)), 0),
        estimatedCloseDate: editingDeal.estimatedCloseDate,
        notes: editingDeal.notes,
        status: editingDeal.status,
      });
      toast.success('Negocio actualizado exitosamente');
      setShowEditDealModal(false);
      setEditingDeal(null);
      loadEmpresa();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al actualizar negocio');
    }
  };

  // Edit empresa functions
  const openEditEmpresaModal = () => {
    setEditingEmpresa({
      name: empresa.name || '',
      documentType: empresa.documentType || 'NIT',
      documentNumber: empresa.documentNumber || '',
      personType: empresa.personType || 'Persona Jurídica',
      email: empresa.email || '',
      phone: empresa.phone || '',
      address: empresa.address || '',
    });
    setShowEditEmpresaModal(true);
  };

  const handleUpdateEmpresa = async (e) => {
    e.preventDefault();
    if (!editingEmpresa) return;

    try {
      await api.updateEmpresa(empresaId, editingEmpresa);
      toast.success('Empresa actualizada exitosamente');
      setShowEditEmpresaModal(false);
      setEditingEmpresa(null);
      loadEmpresa();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al actualizar empresa');
    }
  };

  const addServiceToEditingDeal = (service) => {
    setEditingDeal(prev => ({
      ...prev,
      services: [...prev.services, { ...service, value: service.value || service.minPrice || 0, quantity: 1 }]
    }));
  };

  const updateEditingDealServiceQuantity = (index, quantity) => {
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, quantity: parseInt(quantity) || 1 } : s)
    }));
  };

  const updateEditingDealServiceValue = (index, value) => {
    const numericValue = parseCurrencyToNumber(value);
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, value: numericValue } : s)
    }));
  };

  const removeServiceFromEditingDeal = (index) => {
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const addServiceToDeal = (service) => {
    setNewDeal(prev => ({
      ...prev,
      services: [...prev.services, { ...service, value: service.value || service.minPrice || 0, quantity: 1 }]
    }));
  };

  const updateServiceQuantity = (index, quantity) => {
    setNewDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, quantity: parseInt(quantity) || 1 } : s)
    }));
  };

  const updateServiceValue = (index, value) => {
    const numericValue = parseCurrencyToNumber(value);
    setNewDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, value: numericValue } : s)
    }));
  };

  const removeServiceFromDeal = (index) => {
    setNewDeal(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Empresa no encontrada</p>
          <Button onClick={() => navigate('/empresas')} variant="outline" className="mt-4">
            Volver a Empresas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/empresas')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Empresas
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                {empresa.name?.charAt(0) || 'E'}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">{empresa.name}</h1>
                  <button
                    onClick={openEditEmpresaModal}
                    className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                    title="Editar información de la empresa"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-slate-500">{empresa.documentType}: {empresa.documentNumber}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    empresa.personType === 'Persona Jurídica' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {empresa.personType}
                  </span>
                </div>
                {/* Contact info */}
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                  {empresa.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {empresa.email}
                    </span>
                  )}
                  {empresa.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {empresa.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalContacts || 0}</p>
                <p className="text-sm text-slate-500">Leads</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalDeals || 0}</p>
                <p className="text-sm text-slate-500">Negocios</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.wonDeals || 0}</p>
                <p className="text-sm text-slate-500">Ganados</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{formatValue(stats.totalWonValue || 0)}</p>
                <p className="text-sm text-slate-500">Valor Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
          {['overview', 'contacts', 'deals', 'comercial'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab === 'overview' ? 'Resumen' : tab === 'contacts' ? 'Leads' : tab === 'deals' ? 'Negocios' : 'Comercial'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Información de la Empresa</h2>
              <div className="space-y-3">
                {empresa.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{empresa.email}</span>
                  </div>
                )}
                {empresa.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{empresa.phone}</span>
                  </div>
                )}
                {empresa.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{empresa.address}</span>
                  </div>
                )}
                {empresa.industry && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{empresa.industry}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Últimos Negocios</h2>
              {deals.length > 0 ? (
                <div className="space-y-3">
                  {[...deals].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5).map((deal) => (
                    <div key={deal.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          {deal.code && (
                            <span className="text-xs font-mono text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">{deal.code}</span>
                          )}
                          <p className="font-medium text-slate-900">{deal.services?.length || 0} servicios</p>
                        </div>
                        <p className="text-xs text-slate-500">
                          {deal.createdAt ? new Date(deal.createdAt).toLocaleDateString('es-CO') : 'Sin fecha'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{formatValue(deal.totalValue)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          deal.status === 'won' ? 'bg-green-100 text-green-700' :
                          deal.status === 'lost' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {deal.status === 'won' ? 'Ganado' : deal.status === 'lost' ? 'Perdido' : 'Negociación'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No hay negocios aún</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Leads ({contacts.length})</h2>
              <Button onClick={() => setShowNewContactModal(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </div>
            {contacts.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {[...contacts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => navigate(`/lead/${contact.id}`)}
                    className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{contact.name}</p>
                        <p className="text-sm text-slate-500">{contact.email || contact.phone || 'Sin contacto'}</p>
                        {contact.createdAt && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Calendar className="w-3 h-3" />
                            Registrado: {new Date(contact.createdAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      contact.stageId === 'cerrado' ? 'bg-green-100 text-green-700' :
                      contact.stageId === 'perdido' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {getStageName(contact.pipelineId, contact.stageId)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No hay leads registrados</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'deals' && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Negocios ({deals.length})</h2>
              <p className="text-sm text-slate-500">Para crear un negocio, ve al lead correspondiente</p>
            </div>
            {deals.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {deals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((deal, index) => (
                  <div key={deal.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          deal.status === 'won' ? 'bg-green-500' :
                          deal.status === 'lost' ? 'bg-red-500' : 'bg-orange-500'
                        }`}></div>
                        {deal.code && (
                          <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded font-semibold">{deal.code}</span>
                        )}
                        <span className="font-medium text-slate-900">{formatValue(deal.totalValue)}</span>
                        {/* First Deal / Recurring Badge */}
                        {index === 0 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                            1er Negocio
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-cyan-100 text-cyan-700">
                            Recurrente #{index + 1}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditDealModal(deal)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Editar negocio"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          deal.status === 'won' ? 'bg-green-100 text-green-700' :
                          deal.status === 'lost' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {deal.status === 'won' ? 'Ganado' : deal.status === 'lost' ? 'Perdido' : 'En Negociación'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {deal.services?.map((service, idx) => (
                        <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                          {service.name} x{service.quantity}
                        </span>
                      ))}
                    </div>
                    {deal.leadName && (
                      <p className="text-xs text-slate-500 mt-2">Lead: {deal.leadName}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {deal.createdAt ? new Date(deal.createdAt).toLocaleDateString('es-CO') : 'Sin fecha'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No hay negocios registrados</p>
              </div>
            )}
          </div>
        )}

        {/* Comercial Tab - Shows commercials from leads */}
        {activeTab === 'comercial' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Comerciales Asignados</h2>
              <p className="text-sm text-slate-500 mt-1">
                Comerciales responsables de los leads de esta empresa
              </p>
            </div>
            
            {/* List of commercials from leads */}
            {leadCommercials.length > 0 ? (
              <div className="space-y-3">
                {leadCommercials.map((rep) => {
                  // Count how many leads this commercial has in this empresa
                  const leadsCount = contacts.filter(c => c.assignedTo === rep.id).length;
                  return (
                    <div key={rep.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {rep.name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{rep.name}</p>
                          <p className="text-sm text-slate-500">{rep.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          {leadsCount} {leadsCount === 1 ? 'lead' : 'leads'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <User className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No hay comerciales asignados a los leads de esta empresa</p>
                <p className="text-xs text-slate-400 mt-1">
                  Asigna comerciales desde la vista de cada lead
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Contact Modal */}
      <Dialog open={showNewContactModal} onOpenChange={(open) => {
        setShowNewContactModal(open);
        if (!open) {
          setContactMode('new');
          setLeadSearchQuery('');
          setLeadSearchResults([]);
          setSelectedExistingLead(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Agregar Lead Existente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Buscar Lead
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={leadSearchQuery}
                    onChange={(e) => setLeadSearchQuery(e.target.value)}
                    placeholder="Nombre, email o teléfono..."
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Search Results */}
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg">
                {searchingLeads ? (
                  <div className="p-4 text-center text-slate-500">
                    <div className="animate-spin w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    Buscando...
                  </div>
                ) : leadSearchResults.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {leadSearchResults.map(lead => (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => setSelectedExistingLead(lead)}
                        className={`w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors ${
                          selectedExistingLead?.id === lead.id ? 'bg-emerald-50 border-l-4 border-emerald-600' : ''
                        }`}
                      >
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.email || lead.phone || 'Sin lead'}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : leadSearchQuery.length >= 2 ? (
                  <div className="p-4 text-center text-slate-500">
                    No se encontraron leads
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-400 text-sm">
                    Escriba al menos 2 caracteres para buscar
                  </div>
                )}
              </div>
              
              {selectedExistingLead && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm font-medium text-emerald-900">Lead seleccionado:</p>
                  <p className="text-emerald-700">{selectedExistingLead.name}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNewContactModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="button" 
                  onClick={handleAssociateExistingLead}
                  disabled={!selectedExistingLead}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Asociar Lead
                </Button>
              </div>
            </div>
        </DialogContent>
      </Dialog>

      {/* New Deal Modal */}
      <Dialog open={showNewDealModal} onOpenChange={setShowNewDealModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-600" />
              Nuevo Negocio
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateDeal} className="space-y-4 pt-4">
            {/* Contact selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Lead Responsable <span className="text-red-500">*</span>
              </label>
              {contacts.length > 0 ? (
                <select
                  value={newDeal.leadId}
                  onChange={(e) => setNewDeal(prev => ({ ...prev, leadId: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  required
                >
                  <option value="">Seleccione un lead</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.id}>{contact.name} {contact.email ? `(${contact.email})` : ''}</option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <p>No hay leads asociados a esta empresa.</p>
                  <button 
                    type="button"
                    onClick={() => { setShowNewDealModal(false); setShowNewContactModal(true); }}
                    className="text-amber-800 underline mt-1"
                  >
                    Agregar un lead primero
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Servicios <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {services.filter(s => !newDeal.services.find(ds => ds.id === s.id)).map(service => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => addServiceToDeal(service)}
                    className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-full transition-colors"
                  >
                    + {service.name}
                  </button>
                ))}
              </div>
              
              {newDeal.services.length > 0 && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  {newDeal.services.map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium flex-1">{service.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">Cant:</span>
                          <input
                            type="number"
                            min="1"
                            value={service.quantity}
                            onChange={(e) => updateServiceQuantity(idx, e.target.value)}
                            className="w-16 px-2 py-1 border border-slate-200 rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">Valor:</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberAsCurrency(service.value || 0)}
                            onChange={(e) => updateServiceValue(idx, e.target.value)}
                            className="w-32 px-2 py-1 border border-slate-200 rounded text-sm text-right"
                            placeholder="0"
                          />
                        </div>
                        <span className="text-sm font-medium text-emerald-600 w-24 text-right">
                          {formatValue(service.value * service.quantity)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeServiceFromDeal(idx)}
                          className="text-red-500 hover:text-red-700 font-bold"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-200 flex justify-between font-semibold">
                    <span>Total:</span>
                    <span className="text-green-600">
                      {formatValue(newDeal.services.reduce((acc, s) => acc + (s.value * s.quantity), 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Estimada de Cierre</label>
              <Input
                type="date"
                value={newDeal.estimatedCloseDate}
                onChange={(e) => setNewDeal(prev => ({ ...prev, estimatedCloseDate: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
              <textarea
                value={newDeal.notes}
                onChange={(e) => setNewDeal(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                rows={2}
                placeholder="Notas adicionales..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white pb-2">
              <Button type="button" variant="outline" onClick={() => setShowNewDealModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Crear Negocio
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Deal Modal */}
      <Dialog open={showEditDealModal} onOpenChange={setShowEditDealModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-emerald-600" />
              Editar Negocio
            </DialogTitle>
          </DialogHeader>
          
          {editingDeal && (
            <form onSubmit={handleUpdateDeal} className="space-y-4 pt-4">
              {/* Lead info (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Lead Responsable
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
                  {editingDeal.leadName || editingDeal.contactName || 'Sin asignar'}
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Servicios <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {services.filter(s => !editingDeal.services?.find(ds => ds.id === s.id)).map(service => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => addServiceToEditingDeal(service)}
                      className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-full transition-colors"
                    >
                      + {service.name}
                    </button>
                  ))}
                </div>
                
                {editingDeal.services?.length > 0 && (
                  <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    {editingDeal.services.map((service, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium flex-1">{service.name}</span>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Cant:</span>
                            <input
                              type="number"
                              min="1"
                              value={service.quantity}
                              onChange={(e) => updateEditingDealServiceQuantity(idx, e.target.value)}
                              className="w-16 px-2 py-1 border border-slate-200 rounded text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Valor:</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatNumberAsCurrency(service.value || service.minPrice || 0)}
                              onChange={(e) => updateEditingDealServiceValue(idx, e.target.value)}
                              className="w-32 px-2 py-1 border border-slate-200 rounded text-sm text-right"
                              placeholder="0"
                            />
                          </div>
                          <span className="text-sm font-medium text-emerald-600 w-24 text-right">
                            {formatValue((service.value || service.minPrice || 0) * service.quantity)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeServiceFromEditingDeal(idx)}
                            className="text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-slate-200 flex justify-between font-semibold">
                      <span>Total:</span>
                      <span className="text-green-600">
                        {formatValue(editingDeal.services.reduce((acc, s) => acc + ((s.value || s.minPrice || 0) * s.quantity), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Estimada de Cierre</label>
                <Input
                  type="date"
                  value={editingDeal.estimatedCloseDate || ''}
                  onChange={(e) => setEditingDeal(prev => ({ ...prev, estimatedCloseDate: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea
                  value={editingDeal.notes || ''}
                  onChange={(e) => setEditingDeal(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  rows={2}
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white pb-2">
                <Button type="button" variant="outline" onClick={() => setShowEditDealModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Empresa Modal */}
      <Dialog open={showEditEmpresaModal} onOpenChange={setShowEditEmpresaModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Editar Información de la Empresa
            </DialogTitle>
          </DialogHeader>
          
          {editingEmpresa && (
            <form onSubmit={handleUpdateEmpresa} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre de la Empresa <span className="text-red-500">*</span>
                </label>
                <Input
                  value={editingEmpresa.name}
                  onChange={(e) => setEditingEmpresa(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre de la empresa"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Tipo Documento
                  </label>
                  <select
                    value={editingEmpresa.documentType}
                    onChange={(e) => setEditingEmpresa(prev => ({ 
                      ...prev, 
                      documentType: e.target.value,
                      personType: e.target.value === 'NIT' ? 'Persona Jurídica' : 'Persona Natural'
                    }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  >
                    <option value="NIT">NIT</option>
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Número Documento
                  </label>
                  <Input
                    value={editingEmpresa.documentNumber}
                    onChange={(e) => setEditingEmpresa(prev => ({ ...prev, documentNumber: e.target.value }))}
                    placeholder="Número de documento"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de Persona</label>
                <select
                  value={editingEmpresa.personType}
                  onChange={(e) => setEditingEmpresa(prev => ({ ...prev, personType: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  disabled
                >
                  <option value="Persona Natural">Persona Natural</option>
                  <option value="Persona Jurídica">Persona Jurídica</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">Asignado automáticamente según el tipo de documento</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={editingEmpresa.email}
                    onChange={(e) => setEditingEmpresa(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@empresa.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <Input
                    value={editingEmpresa.phone}
                    onChange={(e) => setEditingEmpresa(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+57 300 000 0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dirección</label>
                <Input
                  value={editingEmpresa.address}
                  onChange={(e) => setEditingEmpresa(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Dirección de la empresa"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditEmpresaModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
